import { Archive, ArrowLeft, FileText, Lock, Pencil, RotateCcw } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DetailColumns } from '@/components/ui/detail-columns';
import type { ApiError } from '@/core/api';
import { useAssistantEntity } from '@/features/assistant';
import { AuditTimelinePanel } from '@/features/audit';
import { CommentsPanel } from '@/features/comments';
import { ActivitiesPanel } from '@/features/crm/activities';
import { useCompany } from '@/features/crm/companies/queries';
import { AttachmentsPanel } from '@/features/documents';
import { CustomFieldsPanel } from '@/features/custom-fields';
import { TagsPanel } from '@/features/tags';
import { useCreateProposal, useProposals } from '@/features/crm/proposals/queries';
import { canSeeMoney, formatMoney } from '@/lib/money';
import { useSession } from '@/stores/session';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { IcpFitBadge } from '@/features/crm/leads/components/IcpFitBadge';
import {
  commissionBasisLabel,
  DEAL_STAGES,
  lostReasonLabel,
  stageLabel,
  stageVariant,
} from '../constants';
import { useArchiveDeal, useDeal, usePurgeDeal, useRestoreDeal, useUpdateDeal } from '../queries';

export function DealDetailPage() {
  const { publicId = '' } = useParams();
  const dealId = publicId;
  const navigate = useNavigate();
  const role = useSession(s => s.role);
  const moneyVisible = canSeeMoney(role);

  const { data: deal, isPending, isError, error } = useDeal(dealId);
  const company = useCompany(deal?.company ?? -1);
  const archive = useArchiveDeal();
  const restore = useRestoreDeal();
  const purge = usePurgeDeal();
  const createProposal = useCreateProposal();
  const updateDeal = useUpdateDeal(dealId);
  // A deal gets at most one proposal — once one exists, hide the create action.
  // Wait for the lookup to resolve before showing the button so it doesn't flash
  // in then vanish on deals that already have a proposal.
  const dealProposals = useProposals({ deal: deal?.id }, deal?.id != null && moneyVisible);
  const proposalsLoaded = !dealProposals.isPending;
  const hasProposal = (dealProposals.data?.count ?? 0) > 0;
  const canCreateProposal = moneyVisible && proposalsLoaded && !hasProposal;

  async function handleCreateProposal() {
    if (!deal) return;
    // Draft a proposal from this deal and jump to it to add line items
    // (mirrors Proposal → Contract). Money-gated, so only money roles see it.
    const proposal = await createProposal.mutateAsync({
      deal: deal.id,
      company: deal.company,
      title: deal.name,
      status: 'draft',
      currency: 'USD',
      notes: '',
    });
    // Advance the pipeline to "Proposal" — but only forward, so a deal already
    // at negotiation/won/lost isn't dragged backwards.
    if (stageOrder(deal.stage) < stageOrder('proposal')) {
      await updateDeal.mutateAsync({ stage: 'proposal' });
    }
    navigate(`/crm/proposals/${proposal.public_id}`);
  }

  // Wave 10 — scope the floating assistant to this deal while the page is mounted.
  useAssistantEntity(
    deal ? { entity: 'crm.deal', entity_id: String(deal.id), label: `Deal: ${deal.name}` } : null
  );

  async function handleArchive() {
    await archive.mutateAsync(dealId);
    navigate('/crm/deals');
  }

  function handleRestore() {
    restore.mutate(dealId);
  }

  function handlePurge() {
    purge.mutate(dealId, { onSuccess: () => navigate('/crm/deals') });
  }

  if (isPending) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm/deals')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const money = formatMoney(deal.amount, deal.currency);

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/deals')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{deal.name}</h1>
          <Badge variant={stageVariant(deal.stage)}>{stageLabel(deal.stage)}</Badge>
          {deal.is_open ? <Badge variant="outline">Open</Badge> : null}
        </div>
        <div className="flex gap-2">
          {canCreateProposal ? (
            <Button onClick={handleCreateProposal} disabled={createProposal.isPending}>
              <FileText /> {createProposal.isPending ? 'Creating…' : 'Create proposal'}
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate(`/crm/deals/${deal.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" aria-label="Archive deal">
                <Archive /> Archive
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive this deal?</AlertDialogTitle>
                <AlertDialogDescription>
                  Hide this row from the active list. You can restore it later from Archived view.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive} disabled={archive.isPending}>
                  {archive.isPending ? 'Archiving…' : 'Archive'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {deal.is_archived ? (
        <Card className="bg-muted/40 border-dashed">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="text-sm">
              <p className="font-medium">This deal is archived.</p>
              <p className="text-muted-foreground">
                Restore it to make it visible in the active list again.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleRestore}
                disabled={restore.isPending}
                aria-label="Restore deal"
              >
                <RotateCcw /> {restore.isPending ? 'Restoring…' : 'Restore'}
              </Button>
              <PurgeArchivedButton
                variant="full"
                name={deal.name}
                onPurge={handlePurge}
                pending={purge.isPending}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <DetailColumns
        main={
          <>
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label="Company">{company.data?.name ?? '…'}</Field>
                <Field label="Owner">{deal.owner_name ?? '—'}</Field>
                <Field label="Estimated value">
                  {money ??
                    (moneyVisible ? (
                      '—'
                    ) : (
                      <span className="text-muted-foreground inline-flex items-center gap-1">
                        <Lock className="size-3" /> Restricted for your role
                      </span>
                    ))}
                </Field>
                <Field label="Probability">{Number(deal.probability)}%</Field>
                {moneyVisible ? (
                  <Field label="Commission">
                    {deal.commission_basis === 'none' ? (
                      'No commission'
                    ) : deal.commission_pct != null ? (
                      <span className="flex flex-col">
                        <span>{Number(deal.commission_pct)}%</span>
                        <span className="text-muted-foreground text-xs">
                          {commissionBasisLabel(deal.commission_basis)}
                        </span>
                      </span>
                    ) : (
                      '—'
                    )}
                  </Field>
                ) : null}
                <Field label="Expected close">
                  {deal.expected_close_date
                    ? new Date(deal.expected_close_date).toLocaleDateString()
                    : '—'}
                </Field>
                <Field label="Created">{new Date(deal.created).toLocaleDateString()}</Field>
                {deal.stage === 'lost' ? (
                  <>
                    <Field label="Lost reason">
                      {deal.lost_reason ? lostReasonLabel(deal.lost_reason) : '—'}
                    </Field>
                    <Field label="Lost note">{deal.lost_note || '—'}</Field>
                  </>
                ) : null}
              </CardContent>
            </Card>

            {deal.origin_lead ? (
              <Card>
                <CardHeader>
                  <CardTitle>From lead</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  <Field label="Lead">
                    <Link
                      to={`/crm/leads/${deal.origin_lead.public_id}`}
                      className="text-primary hover:underline"
                    >
                      {deal.origin_lead.title}
                    </Link>
                  </Field>
                  <Field label="ICP fit">
                    <IcpFitBadge
                      fit={deal.origin_lead.icp_fit}
                      segmentName={deal.origin_lead.icp_segment_name}
                    />
                  </Field>
                  <Field label="Pain" className="sm:col-span-2">
                    {deal.origin_lead.pain || '—'}
                  </Field>
                </CardContent>
              </Card>
            ) : null}

            <CustomFieldsPanel entity="crm.deal" entityId={deal.id} />

            <AttachmentsPanel modelLabel="crm.deal" objectId={deal.id} />

            <CommentsPanel modelLabel="crm.deal" objectId={deal.id} />
          </>
        }
        aside={
          <>
            <TagsPanel modelLabel="crm.deal" objectId={deal.id} />

            <ActivitiesPanel
              scope={{ entity: 'deal', entityId: deal.id }}
              relatedDefaults={{
                company: deal.company,
                contact: deal.primary_contact,
                lead: deal.lead,
              }}
            />
            <AuditTimelinePanel entity="crm.deal" entityId={deal.id} />
          </>
        }
      />
    </div>
  );
}

/** Position of a stage in the pipeline; unknown stages sort first. */
function stageOrder(stage: string): number {
  return DEAL_STAGES.findIndex((s) => s.value === stage);
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-muted-foreground text-xs font-medium uppercase">{label}</p>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  );
}
